import React, { useState } from 'react';
import { CheckCircle, AlertCircle, ExternalLink, Copy, Globe, Mail, Shield, ArrowRight, ArrowLeft } from 'lucide-react';

const WhiteLabelSetupWizard = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState({
    domain: '',
    emailSender: '',
    domainVerified: false,
    emailConfigured: false
  });

  const steps = [
    {
      id: 1,
      title: 'Domain Configuration',
      description: 'Set up your custom domain for the white-label portal'
    },
    {
      id: 2,
      title: 'Email Sender Setup',
      description: 'Configure your custom email sender for notifications'
    },
    {
      id: 3,
      title: 'DNS Configuration',
      description: 'Complete DNS setup for your domain'
    },
    {
      id: 4,
      title: 'Verification',
      description: 'Verify your setup is working correctly'
    }
  ];

  if (!isOpen) return null;

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete(setupData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
          <h2 className="text-2xl font-bold text-white">White Label Setup Wizard</h2>
          <p className="text-purple-100 mt-1">Configure your custom domain and email settings</p>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                  currentStep >= step.id
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {currentStep > step.id ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    step.id
                  )}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-12 h-1 mx-2 ${
                    currentStep > step.id ? 'bg-purple-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-2">
            <h3 className="font-semibold text-gray-900">{steps[currentStep - 1].title}</h3>
            <p className="text-sm text-gray-600">{steps[currentStep - 1].description}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">Custom Domain Setup</h4>
                </div>
                <p className="text-blue-800 text-sm">
                  Enter your custom domain where clients will access their portal
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Domain
                </label>
                <input
                  type="text"
                  value={setupData.domain}
                  onChange={(e) => setSetupData({...setupData, domain: e.target.value})}
                  placeholder="portal.yourcompany.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  This will be where clients access their portal (e.g., portal.yourcompany.com)
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <h4 className="font-semibold text-yellow-900">Important Note</h4>
                </div>
                <p className="text-yellow-800 text-sm">
                  You'll need to configure DNS settings with your domain provider. We'll provide specific instructions in the next steps.
                </p>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Mail className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-green-900">Email Sender Configuration</h4>
                </div>
                <p className="text-green-800 text-sm">
                  Set up the email address that will send notifications to clients
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sender Email Address
                </label>
                <input
                  type="email"
                  value={setupData.emailSender}
                  onChange={(e) => setSetupData({...setupData, emailSender: e.target.value})}
                  placeholder="notifications@yourcompany.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-sm text-gray-500 mt-1">
                  All automated emails will be sent from this address
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Email Types That Will Be Sent:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Task notifications and updates</li>
                  <li>• Project completion alerts</li>
                  <li>• Client portal invitations</li>
                  <li>• Password reset emails</li>
                  <li>• System announcements</li>
                </ul>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  <h4 className="font-semibold text-purple-900">DNS Configuration Required</h4>
                </div>
                <p className="text-purple-800 text-sm">
                  Add these DNS records to your domain provider
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">DNS Records to Add:</h4>
                
                <div className="space-y-3">
                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">CNAME Record</span>
                      <button className="flex items-center space-x-1 text-blue-600 text-sm hover:text-blue-800">
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </button>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p><strong>Host:</strong> {setupData.domain || 'portal.yourcompany.com'}</p>
                      <p><strong>Points to:</strong> app.projectathena.us</p>
                      <p><strong>TTL:</strong> 300 (or default)</p>
                    </div>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">MX Record (for email)</span>
                      <button className="flex items-center space-x-1 text-blue-600 text-sm hover:text-blue-800">
                        <Copy className="w-4 h-4" />
                        <span>Copy</span>
                      </button>
                    </div>
                    <div className="text-sm text-gray-600">
                      <p><strong>Host:</strong> @ (root domain)</p>
                      <p><strong>Points to:</strong> smtp.sendgrid.net</p>
                      <p><strong>Priority:</strong> 10</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  <strong>Note:</strong> DNS changes can take up to 24 hours to propagate. We'll verify the setup in the next step.
                </p>
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">Setup Verification</h4>
                </div>
                <p className="text-blue-800 text-sm">
                  Let's verify your white-label configuration is working correctly
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-gray-900">Domain: {setupData.domain}</span>
                  </div>
                  <span className="text-sm text-green-600">Configured</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-gray-900">Email: {setupData.emailSender}</span>
                  </div>
                  <span className="text-sm text-green-600">Configured</span>
                </div>

                <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <AlertCircle className="w-5 h-5 text-yellow-500" />
                    <span className="text-gray-900">DNS Propagation</span>
                  </div>
                  <span className="text-sm text-yellow-600">Pending</span>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Next Steps:</h4>
                <ul className="text-sm text-green-800 space-y-1">
                  <li>1. Your white-label settings have been saved</li>
                  <li>2. DNS changes may take up to 24 hours to take effect</li>
                  <li>3. You'll receive an email confirmation once everything is verified</li>
                  <li>4. Test your client portal at: <a href={`https://${setupData.domain}`} className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">{setupData.domain}</a></li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>

          <div className="flex items-center space-x-3">
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Previous</span>
              </button>
            )}

            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={
                  (currentStep === 1 && !setupData.domain) ||
                  (currentStep === 2 && !setupData.emailSender)
                }
                className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <span>Next</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Complete Setup</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhiteLabelSetupWizard;
